import {
  clearFidexaHubSessionEndReason,
  clearFidexaHubSessionToken,
  getFidexaHubSessionEndReason,
  getFidexaHubSessionToken,
  setFidexaHubMemberId,
  setFidexaHubSessionEndReason,
  setFidexaHubSessionToken,
  setFidexaHubUnauthorizedHandler,
} from "@/api/fidexaHub";
import { fidexaHubAuthService } from "@/domain/fidexaHub/fidexaHubAuthService";
import { disablePushOnSessionEnd } from "@/domain/fidexaHub/fidexaHubPush";
import type { VerifyFidexaHubOtpType } from "@/domain/fidexaHub/fidexaHubAuthType";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import {
  FidexaHubAuthContext,
  type FidexaHubSessionEndReason,
} from "./useFidexaHubAuth";

// Provider separado do AuthProvider de agente (src/context/auth) — a
// MemberSession do FidexaHub é um bearer opaco de longa duração (30-90
// dias), sem refresh token, então não reaproveita o interceptor/lock de
// renovação do `api` principal. Ver src/api/fidexaHub.ts.
export const FidexaHubAuthProvider = ({ children }: React.PropsWithChildren) => {
  const queryClient = useQueryClient();
  const [sessionToken, setSessionToken] = useState<string | null>(() =>
    getFidexaHubSessionToken()
  );
  // Lido do storage no boot: o motivo precisa sobreviver ao F5 — recarregar
  // é a primeira reação de quem acha que o login não funcionou, e um aviso
  // que some justamente aí devolveria o formulário mudo que este mecanismo
  // existe pra evitar.
  const [sessionEndReason, setSessionEndReasonState] =
    useState<FidexaHubSessionEndReason | null>(
      () => getFidexaHubSessionEndReason() as FidexaHubSessionEndReason | null
    );

  const setSessionEndReason = (
    reason: FidexaHubSessionEndReason | null
  ) => {
    if (reason) {
      setFidexaHubSessionEndReason(reason);
    } else {
      clearFidexaHubSessionEndReason();
    }
    setSessionEndReasonState(reason);
  };

  // Todo dado do FidexaHub (chats, mensagens, perfil, amigos) fica em
  // cache do React Query sob chaves globais ("fidexa-hub-*"), nunca
  // escopadas por member — sem isso, trocar de Member NA MESMA ABA (login
  // como outro member depois de um logout, ou resgatando outro magic
  // link/OTP) deixava a tela reaproveitando dados do member ANTERIOR até
  // um F5 forçar refetch. Vazamento real de conversa privada entre
  // members, não só staleness visual — reproduzido ao vivo: um member novo
  // via a conversa completa de outro member com uma empresa.
  const clearFidexaHubQueryCache = () => {
    queryClient.removeQueries({
      predicate: query =>
        typeof query.queryKey[0] === "string" &&
        query.queryKey[0].startsWith("fidexa-hub"),
    });
  };

  const setSession = (token: string, memberId: string) => {
    clearFidexaHubQueryCache();
    setFidexaHubSessionToken(token);
    setFidexaHubMemberId(memberId);
    setSessionToken(token);
    setSessionEndReason(null);
  };

  const endSession = (reason: FidexaHubSessionEndReason | null) => {
    // Token lido antes de limpar: no logout ele ainda vale pra revogar a
    // inscrição no backend; derrubada pela API ("rejected") ele não vale.
    const endingToken = reason === "rejected" ? null : getFidexaHubSessionToken();
    void disablePushOnSessionEnd(endingToken);
    clearFidexaHubQueryCache();
    clearFidexaHubSessionToken();
    setSessionToken(null);
    setSessionEndReason(reason);
  };

  // Saída por vontade do próprio cliente: não é motivo pra tela de resume
  // avisar nada.
  const logout = () => endSession(null);

  // Chamado quando o cliente recomeça o login de fato (pediu um código
  // novo): o aviso era sobre a sessão anterior e já cumpriu o papel — daí
  // em diante só faria ruído numa tela onde ele já está agindo.
  const clearSessionEndReason = () => setSessionEndReason(null);

  // Conecta o interceptor de 401 (fora da árvore React) a este `logout()` —
  // sem isso, `isAuthenticated` fica desatualizado depois de um 401 e a
  // ProtectedRoute nunca redireciona pro resume (ver src/api/fidexaHub.ts).
  //
  // Carrega o motivo junto: a ProtectedRoute manda todo mundo pro mesmo
  // lugar, e sem isso a sessão derrubada pela API era indistinguível de um
  // logout — o cliente voltava calado pro formulário de telefone, sem
  // saber que precisava entrar de novo.
  useEffect(() => {
    setFidexaHubUnauthorizedHandler(() => endSession("rejected"));
    return () => setFidexaHubUnauthorizedHandler(() => {});
  }, []);

  const { mutate: verifyOtp, isPending: isVerifyingOtp } = useMutation<
    void,
    Error,
    VerifyFidexaHubOtpType
  >({
    mutationFn: async request => {
      const { sessionToken: token, memberId } =
        await fidexaHubAuthService.handleVerifyOtp(request);
      setSession(token, memberId);
    },
  });

  return (
    <FidexaHubAuthContext.Provider
      value={{
        sessionToken,
        isAuthenticated: !!sessionToken,
        // Não há verificação assíncrona de boot (o token não é um JWT com
        // exp local para checar) — a validade real só é conhecida no
        // primeiro request; um 401 já limpa a sessão (ver interceptor).
        isSessionLoaded: true,
        sessionEndReason,
        clearSessionEndReason,
        setSession,
        verifyOtp,
        isVerifyingOtp,
        logout,
      }}
    >
      {children}
    </FidexaHubAuthContext.Provider>
  );
};
